import { ErrorState } from '@/components/common/ErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useFeed } from '@/hooks/useFeed';

import { FeedCard } from './FeedCard';

interface FeedListProps {
  className?: string;
}

export function FeedList({ className }: FeedListProps) {
  const { feedItems, isLoading, error, loadMore, refetch, hasMore } = useFeed();

  // Initial loading state (only when no items loaded yet)
  if (isLoading && feedItems.length === 0) {
    return <LoadingSpinner label="Loading feed" testId="feed-loading" />;
  }

  // Error state (only when no items exist - initial load failed)
  if (error && feedItems.length === 0) {
    return (
      <ErrorState
        message="Failed to load feed. Please try again."
        onPrimaryAction={() => void refetch()}
        testId="feed-error"
      />
    );
  }

  // Empty state
  if (feedItems.length === 0) {
    return (
      <div
        className="flex justify-center items-center py-12"
        data-testid="feed-empty"
      >
        <p className="text-gray-500 text-center">No ratings yet</p>
      </div>
    );
  }

  // Loaded state with list
  return (
    <div
      className={['flex flex-col gap-4', className].filter(Boolean).join(' ')}
      data-testid="feed-list"
    >
      {feedItems.map((item) => (
        <FeedCard key={item.id} item={item} />
      ))}

      {/* Inline error for pagination failures - keeps existing content visible */}
      {error && (
        <div
          className="flex flex-col items-center justify-center py-4 gap-3"
          data-testid="feed-load-more-error"
        >
          <p role="alert" className="text-wolisho-red text-center text-sm">
            Failed to load more items.
          </p>
          <button
            type="button"
            onClick={() => void loadMore()}
            className="px-4 py-2 bg-wolisho-red text-white rounded-lg hover:bg-wolisho-red/90 transition-colors text-sm"
          >
            Try again
          </button>
        </div>
      )}

      {hasMore && !error && (
        <div className="flex justify-center pt-4">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={isLoading}
            className="px-6 py-2 bg-wolisho-red text-white font-semibold rounded-lg hover:bg-wolisho-red/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="load-more-button"
          >
            {isLoading && (
              <div
                className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"
                role="status"
                aria-label="Loading more"
              />
            )}
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
